/**
 * Voluum-style split: left list + right detail/conditions editor.
 * Right pane is selection-driven (default path / rule-group conditions / rule path).
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import type {
  VoluumFlowUpdate,
  VoluumPathGroupUpdate,
  VoluumPathUpdate,
} from '@trafficpulse/shared';

import { defaultPathWeightPercents } from './default-paths';
import PathsLeftColumn from './PathsLeftColumn';
import PathsRightPane from './PathsRightPane';
import { usePathsSelection } from './selection';

export interface StandardPathsWorkspaceProps {
  draft: VoluumFlowUpdate;
  onPatch: (mutator: (d: VoluumFlowUpdate) => void) => void;
}

const StandardPathsWorkspace: FC<StandardPathsWorkspaceProps> = ({
  draft,
  onPatch,
}) => {
  const { selection, setSelection } = usePathsSelection(draft);
  const paths = draft.defaultPaths ?? [];
  const groups = draft.conditionalPathsGroups ?? [];
  const percents = defaultPathWeightPercents(paths);

  const patchDefaultPath = (
    idx: number,
    mutator: (p: VoluumPathUpdate) => void,
  ) =>
    onPatch((d) => {
      const t = d.defaultPaths?.[idx];
      if (t) mutator(t);
    });

  const patchGroup = (
    idx: number,
    mutator: (g: VoluumPathGroupUpdate) => void,
  ) =>
    onPatch((d) => {
      const t = d.conditionalPathsGroups?.[idx];
      if (t) mutator(t);
    });

  const patchGroupPath = (
    groupIdx: number,
    pathIdx: number,
    mutator: (p: VoluumPathUpdate) => void,
  ) =>
    patchGroup(groupIdx, (g) => {
      const t = g.paths?.[pathIdx];
      if (t) mutator(t);
    });

  return (
    <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-border/80 bg-card p-2 shadow-none sm:p-2.5">
      <header className="shrink-0 space-y-2">
        <h3 className="text-xs font-semibold text-foreground">Paths</h3>
        <p className="text-[11px] leading-snug text-muted-foreground">
          Select a default path or a rule-based path to edit details. Select a
          rule group header to edit its conditions.
        </p>
      </header>
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden border-t border-border/60 pt-2 lg:flex-row lg:gap-0">
        <PathsLeftColumn
          draft={draft}
          onPatch={onPatch}
          selection={selection}
          onSelect={setSelection}
          percents={percents}
        />
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto border-border lg:border-l lg:pl-3">
          <PathsRightPane
            selection={selection}
            draft={draft}
            paths={paths}
            groups={groups}
            patchDefaultPath={patchDefaultPath}
            patchGroup={patchGroup}
            patchGroupPath={patchGroupPath}
            onSelectionChange={setSelection}
          />
        </div>
      </div>
    </section>
  );
};

export default StandardPathsWorkspace;
